import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Spinner } from './Feedback.js';
/**
 * The only button.
 *
 * Three behaviours are deliberately not left to call sites, because each is a
 * place products become inconsistent:
 *
 * - **Loading keeps the label mounted** and hides it, so the button does not
 *   resize mid-click and slide the next control under the pointer.
 * - **Loading is also disabled**, and `aria-busy` reports it, so a screen
 *   reader is not told a busy button is ready — and a double submit cannot
 *   place two orders.
 * - **`type` defaults to `button`.** HTML's default is `submit`, which turns
 *   every unmarked button inside a form into an accidental submit.
 */
export function Button({ children, variant = 'primary', size = 'regular', loading = false, disabled = false, icon, fullWidth = false, className, type = 'button', ref, ...rest }) {
    const classes = [
        'lh-button',
        `lh-button--${variant}`,
        size !== 'regular' && `lh-button--${size}`,
        fullWidth && 'lh-button--full',
        loading && 'lh-button--loading',
        className,
    ]
        .filter(Boolean)
        .join(' ');
    return (_jsxs("button", { ref: ref, type: type, className: classes, disabled: disabled || loading, "aria-busy": loading || undefined, ...rest, children: [loading && (_jsx("span", { className: "lh-button__spinner", children: _jsx(Spinner, { size: 18, onFill: variant === 'primary' || variant === 'destructive' }) })), _jsxs("span", { className: "lh-button__label lh-inline lh-gap-xs lh-inline--nowrap", children: [icon, children] })] }));
}
/**
 * A button whose content is only an icon.
 *
 * `label` is required rather than optional, which is the whole point of having
 * a separate component: an icon-only `<button>` is announced as "button" and
 * nothing else, and that cannot be fixed later by a reviewer who does not know
 * what the icon meant.
 */
export function IconButton({ children, label, className, type = 'button', ref, ...rest }) {
    return (_jsx("button", { ref: ref, type: type, className: ['lh-icon-button', className].filter(Boolean).join(' '), "aria-label": label, title: label, ...rest, children: children }));
}
//# sourceMappingURL=Button.js.map